import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { authorizationAPI } from '../../services/api';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const [accessibleResources, setAccessibleResources] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation(['sidebar', 'common']);

  // Dynamic dashboard path based on user role
  const getDashboardPath = () => {
    const role = user?.role?.toUpperCase();
    if (role === 'TEACHER' || role === 'HEADMASTER') {
      return '/school-dashboard';
    } else if (role === 'PPD') {
      return '/ppd-dashboard';
    } else if (role === 'ADMIN' || role === 'SUPERADMIN') {
      return '/admin-dashboard';
    }
    return '/school-dashboard'; // default
  };

  // Menu structure - Reorganized according to new specifications
  const menuItems = [
    {
      section: t('sidebar:sections.dashboard'),
      sectionEn: t('sidebar:sections.dashboard', { lng: 'en' }),
      items: [
        {
          path: getDashboardPath(),
          labelKey: 'sidebar:menu.dashboard',
          icon: 'fa-tachometer-alt',
          resourceCode: null, // Will use dynamic path
          isDynamic: true,
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        }
      ]
    },
    {
      section: t('sidebar:sections.studentManagement'),
      sectionEn: t('sidebar:sections.studentManagement', { lng: 'en' }),
      items: [
        {
          path: '/student-uniform',
          labelKey: 'sidebar:menu.studentUniform',
          icon: 'fa-tshirt',
          resourceCode: '/student-uniform',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'SUPERADMIN']
        },
        {
          path: '/student-master',
          labelKey: 'sidebar:menu.studentMaster',
          icon: 'fa-users',
          resourceCode: '/student-master',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/bulk-promotion',
          labelKey: 'sidebar:menu.studentPromotion',
          icon: 'fa-user-graduate',
          resourceCode: '/bulk-promotion',
          roles: ['HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        }
      ]
    },
    {
      section: t('sidebar:sections.report'),
      sectionEn: t('sidebar:sections.report', { lng: 'en' }),
      items: [
        {
          path: '/reports/school-map',
          labelKey: 'sidebar:menu.schoolMap',
          icon: 'fa-map-marked-alt',
          resourceCode: 'Report-SchoolMap',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-school',
          labelKey: 'sidebar:menu.schoolReport',
          icon: 'fa-file-alt',
          resourceCode: '/report-school',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-summary',
          labelKey: 'sidebar:menu.summaryReport',
          icon: 'fa-chart-pie',
          resourceCode: '/report-summary',
          roles: ['HEADMASTER', 'TEACHER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-student-listing',
          labelKey: 'sidebar:menu.studentListing',
          icon: 'fa-list',
          resourceCode: '/report-student-listing',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-student-list',
          labelKey: 'sidebar:menu.packingListStudent',
          icon: 'fa-table',
          resourceCode: '/report-student-list',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-packing-list-shoes',
          labelKey: 'sidebar:menu.packingListShoes',
          icon: 'fa-shoe-prints',
          resourceCode: '/report-packing-list-shoes',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-packing-list-shoes-by-school',
          labelKey: 'sidebar:menu.packingListShoesBySchool',
          icon: 'fa-shoe-prints',
          resourceCode: '/report-packing-list-shoes-by-school',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-packing-list-uniform',
          labelKey: 'sidebar:menu.packingListUniform',
          icon: 'fa-tshirt',
          resourceCode: '/report-packing-list-uniform',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/report-packing-list-uniform-by-school',
          labelKey: 'sidebar:menu.packingListUniformBySchool',
          icon: 'fa-tshirt',
          resourceCode: '/report-packing-list-uniform-by-school',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        }
      ]
    },
    {
      section: t('sidebar:sections.administration'),
      sectionEn: t('sidebar:sections.administration', { lng: 'en' }),
      items: [
        {
          path: '/user-profile',
          labelKey: 'sidebar:menu.myProfile',
          icon: 'fa-user',
          resourceCode: '/user-profile',
          roles: ['ADMIN', 'HEADMASTER', 'PPD', 'SUPERADMIN', 'TEACHER']
        },
        {
          path: '/user-management',
          labelKey: 'sidebar:menu.userManagement',
          icon: 'fa-user-cog',
          resourceCode: '/user-management',
          roles: ['HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/issue-reports',
          labelKey: 'sidebar:menu.issueManagement',
          icon: 'fa-exclamation-circle',
          resourceCode: '/issue-reports',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/system-audit-log',
          labelKey: 'sidebar:menu.systemAuditLog',
          icon: 'fa-history',
          resourceCode: 'SystemAuditLog',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '/uniform-data-audit-log',
          labelKey: 'sidebar:menu.dataEntryAudit',
          icon: 'fa-clipboard-list',
          resourceCode: 'UniformDataAuditLog',
          roles: ['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']
        },
        {
          path: '#',
          labelKey: 'sidebar:menu.logoff',
          icon: 'fa-sign-out-alt',
          isLogout: true,
          roles: ['ADMIN', 'HEADMASTER', 'PPD', 'SUPERADMIN', 'TEACHER']
        }
      ]
    },
    {
      section: t('sidebar:sections.masterManagement'),
      sectionEn: t('sidebar:sections.masterManagement', { lng: 'en' }),
      items: [
        {
          path: '/school-management',
          labelKey: 'sidebar:menu.schoolManagement',
          icon: 'fa-school',
          resourceCode: 'SuperAdmin-SchoolManagement',
          roles: ['SUPERADMIN']
        },
        {
          path: '/dun-management',
          labelKey: 'sidebar:menu.dunManagement',
          icon: 'fa-map-marker-alt',
          resourceCode: 'SuperAdmin-DUNManagement',
          roles: ['SUPERADMIN']
        },
        {
          path: '/par-management',
          labelKey: 'sidebar:menu.parManagement',
          icon: 'fa-landmark',
          resourceCode: 'SuperAdmin-PARManagement',
          roles: ['SUPERADMIN']
        },
        {
          path: '/school-class-management',
          labelKey: 'sidebar:menu.schoolClassManagement',
          icon: 'fa-chalkboard',
          resourceCode: 'SuperAdmin-SchoolClassManagement',
          roles: ['SUPERADMIN']
        },
        {
          path: '/tahun-management',
          labelKey: 'sidebar:menu.gradeYearManagement',
          icon: 'fa-graduation-cap',
          resourceCode: 'SuperAdmin-TahunManagement',
          roles: ['SUPERADMIN']
        },
        {
          path: '/school-enrol-year-management',
          labelKey: 'sidebar:menu.schoolEnrolYearManagement',
          icon: 'fa-calendar-alt',
          resourceCode: 'SuperAdmin-SchoolEnrolYearManagement',
          roles: ['ADMIN', 'SUPERADMIN']
        },
        {
          path: '/activity-management',
          labelKey: 'sidebar:menu.activityManagement',
          icon: 'fa-tasks',
          resourceCode: '/activity-management',
          roles: ['SUPERADMIN']
        },
        {
          path: '/authorization-management',
          labelKey: 'sidebar:menu.authorization',
          icon: 'fa-shield-alt',
          resourceCode: '/authorization-management',
          roles: ['SUPERADMIN']
        }
      ]
    }
  ];

  useEffect(() => {
    // Fetch accessible resources from API
    const fetchAccessibleResources = async () => {
      try {
        const data = await authorizationAPI.getAccessibleResources();
        // API returns resources as array of strings, convert to objects for easier checking
        const resourceObjects = (data.resources || []).map(code => ({ resourceCode: code }));
        console.log('[Sidebar] Loaded accessible resources:', resourceObjects);
        setAccessibleResources(resourceObjects);
      } catch (error) {
        console.error('[Sidebar] Failed to fetch accessible resources:', error);
        console.error('[Sidebar] Falling back to role-based access only');
        // Set empty array so role-based checks can work
        setAccessibleResources([]);
      }
    };

    if (user) {
      console.log('[Sidebar] User logged in:', user.email, 'Role:', user.role);
      fetchAccessibleResources();
    }
  }, [user]);

  const hasAccess = (item) => {
    // Dynamic dashboard - always accessible if user has a role
    if (item.isDynamic) {
      return item.roles && item.roles.includes(user?.role?.toUpperCase());
    }

    // Check if user has required role
    const hasRole = item.roles && item.roles.includes(user?.role?.toUpperCase());

    // If user doesn't have required role, deny access immediately
    if (item.roles && item.roles.length > 0 && !hasRole) {
      return false;
    }

    // If item has resourceCode, check authorization database
    if (item.resourceCode) {
      const hasResourceAccess = accessibleResources.some(r => r.resourceCode === item.resourceCode);

      // If found in authorization database, use that permission
      if (hasResourceAccess) {
        return true;
      }

      // BACKWARD COMPATIBILITY: If not in auth database but user has required role, allow access
      // This ensures existing functionality works while authorization system is being set up
      if (hasRole) {
        console.log('[Sidebar] Granting access via role for:', item.resourceCode, 'Role:', user?.role);
        return true;
      }

      return false;
    }

    // If no resourceCode specified (legacy items), check role only
    if (hasRole) {
      return true;
    }

    return false;
  };

  // Close menu when clicking on a link
  const handleLinkClick = (hasAccessToItem) => {
    if (hasAccessToItem && onClose) {
      onClose();
    }
  };

  // Handle logout
  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      if (onClose) {
        onClose();
      }
      navigate('/login');
    }
  };

  return (
    <>
      {/* Side Navigation Menu */}
      <div className={`sidebar ${isOpen ? 'open' : ''}`} id="sideMenu">
        {/* User Name at Top */}
        {user && (
          <div className="sidebar-header">
            <div className="user-name-display">{user.name || user.email}</div>
          </div>
        )}

        {/* Scrollable Menu Sections Container */}
        <div className="sidebar-menu-container">
          {/* Menu Sections */}
          {menuItems.map((section, idx) => (
            <div key={idx} className="menu-section">
              <div className="menu-section-title" title={section.sectionEn}>{section.section}</div>

              <ul className="menu-items">
                {section.items.map((item, itemIdx) => {
                  const isActive = location.pathname === item.path;
                  const hasAccessToItem = item.isLogout || hasAccess(item);
                  const malayLabel = t(item.labelKey);
                  const englishLabel = t(item.labelKey, { lng: 'en' });

                  // Special handling for logout button
                  if (item.isLogout) {
                    return (
                      <li key={itemIdx}>
                        <button
                          className="menu-item logout-btn"
                          onClick={handleLogout}
                          title={englishLabel}
                        >
                          <i className={`fas ${item.icon} menu-icon`}></i>
                          <span className="menu-label">{malayLabel}</span>
                        </button>
                      </li>
                    );
                  }

                  return (
                    <li key={itemIdx}>
                      <Link
                        to={hasAccessToItem ? item.path : '#'}
                        className={`menu-item ${isActive ? 'active' : ''} ${!hasAccessToItem ? 'disabled' : ''}`}
                        title={englishLabel}
                        onClick={(e) => {
                          if (!hasAccessToItem) {
                            e.preventDefault();
                          } else {
                            handleLinkClick(hasAccessToItem);
                          }
                        }}
                      >
                        <i className={`fas ${item.icon} menu-icon`}></i>
                        <span className="menu-label">{malayLabel}</span>
                        {!hasAccessToItem && (
                          <i className="fas fa-lock menu-lock" title="Access Denied"></i>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* KTC Logo at Bottom */}
        <div className="sidebar-footer">
          <img
            src="/img/KTC-logo.png"
            alt="Kim Teck Cheong Consolidated Berhad"
            className="sidebar-logo"
          />
        </div>
      </div>

      {/* Overlay when menu is open */}
      <div
        className={`sidebar-overlay ${isOpen ? 'active' : ''}`}
        onClick={onClose}
      ></div>
    </>
  );
};

export default Sidebar;
